import React from 'react';
import {
    BulkDeleteWithConfirmButton,
    Datagrid,
    DatagridBody,
    useListContext,
} from 'react-admin';
import {
    Button,
    TableBody,
    TableCell,
    TableRow,
} from '@material-ui/core';

/*
 * Header commun des Datagrid supprimables.
 *
 * Structure :
 *   1. désignations React-Admin + checkbox "tout sélectionner"
 *   2. accordéon d'actions juste dessous
 *   3. lignes normales du Datagrid
 *
 * La barre d'actions reste visuellement dans la largeur
 * disponible même lorsque le tableau défile horizontalement.
 */
const AdminSelectableBody = (props: any) => {
    const {
        selectedIds = [],
        onUnselectItems,
        resource,
        basePath,
    } = useListContext();

    const count = selectedIds.length;
    const plural = count > 1;

    const cellRef = React.useRef<HTMLTableCellElement | null>(null);
    const scrollParentRef = React.useRef<HTMLElement | null>(null);

    const [metrics, setMetrics] = React.useState({
        width: 0,
        offset: 0,
        headerHeight: 48,
    });

    React.useLayoutEffect(() => {
        const cell = cellRef.current;

        if (!cell) {
            return;
        }

        const findHorizontalScroller = () => {
            let node = cell.parentElement;

            while (
                node &&
                node !== document.body
            ) {
                const style = window.getComputedStyle(node);
                const overflowX = style.overflowX;

                if (
                    node.scrollWidth > node.clientWidth &&
                    (
                        overflowX === 'auto' ||
                        overflowX === 'scroll'
                    )
                ) {
                    return node;
                }

                node = node.parentElement;
            }

            /*
             * Fallback :
             * certains wrappers React-Admin changent leur
             * overflow seulement lorsque la table déborde.
             */
            node = cell.parentElement;

            while (
                node &&
                node !== document.body
            ) {
                if (
                    node.scrollWidth >
                    node.clientWidth + 2
                ) {
                    return node;
                }

                node = node.parentElement;
            }

            return null;
        };

        const updateMetrics = () => {
            const table = cell.closest('table');
            const tableHead = table
                ? table.querySelector('thead')
                : null;

            const headerHeight = tableHead
                ? Math.ceil(
                    tableHead.getBoundingClientRect().height
                )
                : 48;

            const scroller =
                scrollParentRef.current ||
                findHorizontalScroller();

            if (scroller) {
                scrollParentRef.current = scroller;

                setMetrics({
                    width: scroller.clientWidth,
                    offset: scroller.scrollLeft,
                    headerHeight,
                });

                return;
            }

            const rect = cell.getBoundingClientRect();

            setMetrics({
                width: Math.max(
                    0,
                    Math.min(
                        rect.width,
                        window.innerWidth -
                            Math.max(0, rect.left) -
                            8
                    )
                ),
                offset: 0,
                headerHeight,
            });
        };

        updateMetrics();

        const scroller = findHorizontalScroller();

        if (scroller) {
            scrollParentRef.current = scroller;
            scroller.addEventListener(
                'scroll',
                updateMetrics,
                { passive: true }
            );
        }

        window.addEventListener(
            'resize',
            updateMetrics
        );

        const frame = requestAnimationFrame(
            updateMetrics
        );

        return () => {
            cancelAnimationFrame(frame);

            if (scroller) {
                scroller.removeEventListener(
                    'scroll',
                    updateMetrics
                );
            }

            window.removeEventListener(
                'resize',
                updateMetrics
            );
        };
    }, [count]);

    return (
        <>
            {count > 0 && (
                <TableBody>
                    <TableRow
                        style={{
                            height: 56,
                        }}
                    >
                    <TableCell
                        ref={cellRef}
                        colSpan={1000}
                        padding="none"
                        style={{
                            padding: 0,
                            borderBottom: '1px solid #ddd',
                            height: 56,
                            maxHeight: 56,
                            overflow: 'hidden',
                            background: '#fff',
                        }}
                    >
                        <div
                            style={{
                                height: 56,
                                overflow: 'hidden',
                            }}
                        >
                            <div
                                style={{
                                    width:
                                        metrics.width > 0
                                            ? metrics.width
                                            : '100%',
                                    transform:
                                        metrics.offset > 0
                                            ? `translateX(${metrics.offset}px)`
                                            : 'none',
                                    height: 56,
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding:
                                        '6px 12px 6px 16px',
                                    background: '#fff',
                                    fontFamily:
                                        '"Roboto", "Helvetica", "Arial", sans-serif',
                                }}
                            >
                                <strong
                                    style={{
                                        fontSize: 14,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {count} sélectionné
                                    {plural ? 's' : ''}
                                </strong>

                                {count > 0 && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent:
                                                'flex-end',
                                            gap: 8,
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <Button
                                            onClick={
                                                onUnselectItems
                                            }
                                            color="primary"
                                            size="small"
                                        >
                                            Tout décocher
                                        </Button>

                                        <BulkDeleteWithConfirmButton
                                            resource={resource}
                                            basePath={basePath}
                                            selectedIds={
                                                selectedIds
                                            }
                                            label="Supprimer"
                                            confirmTitle={
                                                `Supprimer ${count} élément${
                                                    plural ? 's' : ''
                                                } sélectionné${
                                                    plural ? 's' : ''
                                                } ?`
                                            }
                                            confirmContent="Cette action est définitive."
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </TableCell>
                    </TableRow>
                </TableBody>
            )}

            <DatagridBody {...props} />
        </>
    );
};

/*
 * Material-UI doit reconnaître ce composant
 * comme le body officiel de la table.
 */
(AdminSelectableBody as any).muiName = 'TableBody';

const AdminSelectableDatagrid = (props: any) => (
    <Datagrid
        {...props}
        hasBulkActions
        body={AdminSelectableBody}
    />
);

export default AdminSelectableDatagrid;
